import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  EditOutlined,
  ApiOutlined,
  BulbOutlined,
  MailFilled,
  PhoneFilled,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { Popconfirm, Breadcrumb, Popover, Tag, Tooltip, Button as ButtonAntd, Select } from 'antd';
import history from '~/services/history';
import DefautLayout from '~/pages/_layouts/full';
import Box from '~/components/Box';
import Button from '~/components/Button';
import PageTitle from '~/components/PageTitle';
import Pagination from '~/components/Pagination';
import { Table, TableActions } from '~/components/Table';
import AccountForm from '~components/AccountForm';
import ReportModal from './reportModal';
import { BreadcrumbCustom } from '~/styles/global';
import Filter from './filter';
import { paginationCustomers, deleteCustomer } from '~/services/hooks/customers';
import { store } from '~/store';

const { Option } = Select;


export default function Clients() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});
  const [tableData, setTableData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState({});
  const [reportType, setReportType] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const sortBy = 'companyName';
  const defaultFilter = { field: 'flag', value: 'CLIENT', restriction: 'EQUAL' };
  const { company } = store.getState().user.profile;
  const defaultFilterCompany = { columnJoin: 'company', field: 'id', value: company.id, restriction: 'EQUAL' };
  const [filters, setFilters] = useState([]);

  const fetchData = async page => {
    try{
    setLoading(true);
    const data = await paginationCustomers({
      page: page - 1,
      sortBy,
      filters: [...filters, defaultFilter, defaultFilterCompany],
    });
    setTableData(data.content);
    const metaData = {
      first: data.first,
      last: data.last,
      numberOfElements: data.numberOfElements,
      pageNumber: data.pageable.pageNumber + 1,
      pageSize: data.pageable.pageSize,
      totalElements: data.totalElements,
    };
    setMeta(metaData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async record => {
    await deleteCustomer(record.id);
    fetchData(1);
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy]);

  const handleReport = async (record, reportType) => {
    setSelectedRecord(record);
    setReportType(reportType);
    setShowReportForm(true);
  };

  const handleEdit = record => {
    setSelectedRecord(record);
    setShowForm(true);
  };

  const style = {
    contacts: { color: '#a1a1a1', marginRight: '8px' },
    pageTitle: { marginRight: 'auto', width: '100%' },
    btnNew: {
      col: { marginLeft: 'auto', paddingBottom: '20px' },
      btn: { marginLeft: 'auto' },
    },
  };

  const getProjectTag = (number, flag, record) => {
    if (number === 0) {
      return null;
    }
    const icon = flag === 'PROJECT' ? <BulbOutlined /> : <ApiOutlined />;
    const color = flag === 'PROJECT' ? 'blue' : flag === 'BID' ? 'green' : 'red';
    const pathname = flag === 'PROJECT' ? 'projects-list' : 'bids-list';
    return (
      <Tooltip
        placement="topLeft"
        title={flag === 'PROJECT' ? t('screens:customers.data.viewProjects') : t('screens:leads.data.viewBids')}
      >
        <Tag
          icon={icon}
          color={color}
          style={{ cursor: 'pointer' }}
          onClick={() =>
            history.push({
              pathname: pathname,
              state: { data: record },
            })
          }
        >
          {number}
        </Tag>
      </Tooltip>
    );
  };

  const getTags = record => {
    if (record.projects === undefined) return null;
    let projectsNumber = 0;
    let bidsNumber = 0;
    let projectsArr = [];
    let bidsArr = [];
    let rejectedNumber = 0;
    let rejectedArr = [];

    // count number of flags projects and bids in projects list
    record.projects.forEach(project => {
      if (project.flag === 'PROJECT') {
        projectsNumber++;
        projectsArr.push(project.description);
      } else if (project.flag === 'BID') {
        bidsNumber++;
        bidsArr.push(project.description);
      } else {
        rejectedNumber++;
        rejectedArr.push(project.description);
      }
    });

    return (
      <>
        {getProjectTag(projectsNumber, 'PROJECT', record)}
        {getProjectTag(bidsNumber, 'BID', record)}
        {getProjectTag(rejectedNumber, 'REJECTED', record)}
      </>
    );
  };

  const tableColumns = [
    {
      title: t('screens:customers.data.companyName'),
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text, record) => (
        <ButtonAntd
          type="link"
          onClick={() =>
            history.push({
              pathname: '/projects-list',
              state: { data: record },
            })
          }
          title={t('messages:view')}
        >
          {text}
        </ButtonAntd>
      ),
    },
    {
      title: t('screens:customers.data.primaryContact'),
      dataIndex: 'customerContact',
      key: 'primaryContact',
      render: customerContact => (
        <>
          {customerContact !== null ? (
            <>
              <div>
                <UserOutlined style={style.contacts} />
                {customerContact.primaryContact.firstName + ' ' + customerContact.primaryContact.lastName}
              </div>
              {customerContact.primaryContact.mainPhone !== null && customerContact.primaryContact.mainPhone !== '' ? (
                <div>
                  <PhoneFilled style={style.contacts} />
                  {customerContact.primaryContact.mainPhone}
                </div>
              ) : (
                ''
              )}
              {customerContact.primaryContact.mainEmail !== null ? (
                <div>
                  <MailFilled style={style.contacts} />
                  {customerContact.primaryContact.mainEmail}
                </div>
              ) : (
                ''
              )}
            </>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: t('screens:leads.data.secondaryContact'),
      dataIndex: 'customerContact',
      key: 'secondaryContact',
      render: customerContact => (
        <>
          {customerContact !== null && customerContact.secondaryContact !== null ? (
            <>
              <>
                <div>
                  <UserOutlined style={style.contacts} />
                  {customerContact.secondaryContact.firstName + ' ' + customerContact.secondaryContact.lastName}
                </div>
                {customerContact.secondaryContact.mainPhone !== null &&
                customerContact.secondaryContact.mainPhone !== '' ? (
                  <div>
                    <PhoneFilled style={style.contacts} />
                    {customerContact.secondaryContact.mainPhone}
                  </div>
                ) : (
                  ''
                )}
                {customerContact.secondaryContact.mainEmail !== null &&
                customerContact.secondaryContact.mainEmail !== '' ? (
                  <div>
                    <MailFilled style={style.contacts} />
                    {customerContact.secondaryContact.mainEmail}
                  </div>
                ) : (
                  ''
                )}
              </>
            </>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: t('screens:customers.data.projects'),
      key: 'projects',
      render: record => getTags(record),
    },
    {
      title: t('screens:customers.data.actions'),
      key: 'actions',
      align: 'center',
      width: 250,
      className: 'table-action',
      render: (text, record) => (
        <TableActions>
          <Button
            onClick={() =>
              history.push({
                pathname: '/projects-list',
                state: { data: record },
              })
            }
            title="View Client Projects"
          >
            <BulbOutlined />
          </Button>
          <Button onClick={() => handleEdit(record)} title={t('messages:edit')}>
            <EditOutlined />
          </Button>

          <Popconfirm
            icon={false}
            title={t('messages:confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('messages:yes')}
            cancelText={t('messages:no')}
          >
            <Button title={t('messages:delete')}>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
          <Popover
            content={
              <Select
                placeholder={t('screens:reports.chooseReport')}
                style={{ width: 240 }}
                onChange={reportType => handleReport(record, reportType)}
                dropdownMatchSelectWidth={false}
              >
                <Option value="ProfitAndLoss">{t('screens:reports.profitAndLoss')}</Option>
                <Option value="ProfitAndLossDetail">{t('screens:reports.profitAndLossDetail')}</Option>
                <Option value="CashFlow">{t('screens:reports.cashFlow')}</Option>
              </Select>
            }
            title="Reports"
            trigger="click"
          >
            <Button>
              <FileTextOutlined />
            </Button>
          </Popover>
        </TableActions>
      ),
    },
  ];

  const breadcrumb = (
    <BreadcrumbCustom>
      <Breadcrumb.Item href="/">{t('menus:home')}</Breadcrumb.Item>
      <Breadcrumb.Item>{t('menus:projects')}</Breadcrumb.Item>
      <Breadcrumb.Item>{t('menus:clients')}</Breadcrumb.Item>
    </BreadcrumbCustom>
  );
  return (
    <DefautLayout breadcrumb={breadcrumb}>
      <PageTitle title={t('menus:clients')} style={style.pageTitle} />
      <Filter isLoading={loading} setFilters={setFilters} />
      <Box>
        <Table
          bordered
          rowKey="id"
          pagination={false}
          loading={loading}
          columns={tableColumns}
          dataSource={tableData}
        />

        <Pagination
          onChange={value => {
            fetchData(value);
          }}
          meta={meta}
        />
      </Box>
        <>
          {showForm && (
            <AccountForm
              record={selectedRecord}
              visible={showForm}
              onClose={() => {
                setShowForm(false);
                setSelectedRecord({});
              }}
            />
          )}
          {showReportForm && (
            <ReportModal
              visible={showReportForm}
              onClose={() => {
                setShowReportForm(false);
                setSelectedRecord({});
              }}
              record={selectedRecord}
              reportType={reportType}
            />
          )}
        </>
    </DefautLayout>
  );
}
